import { useTrialStatus } from "@/hooks/useTrialStatus";
import { useSubscription } from "@/hooks/useSubscription";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Sparkles, Clock, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

export const TrialBanner = () => {
  const { t } = useTranslation();
  const { isInTrial, daysRemaining, trialExpired } = useTrialStatus();
  const { subscription } = useSubscription();
  
  const currentTier = subscription?.data?.currentTier || 'free';
  
  // Show read-only banner when: trial expired + user is on free tier
  const showReadOnlyBanner = !isInTrial && trialExpired && currentTier === 'free';

  // Don't show any banner if not in trial and not read-only
  if (!isInTrial && !showReadOnlyBanner) return null;

  // Read-Only Mode Banner (Trial Expired)
  if (showReadOnlyBanner) {
    return (
      <div className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white py-2.5 px-4 shadow-lg animate-in slide-in-from-top duration-300">
        <div className="container mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Lock className="h-5 w-5" />
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
              <span className="text-sm font-semibold">
                {t('trial.readOnlyBannerTitle')}
              </span>
              <span className="text-xs sm:text-sm opacity-90">
                {t('trial.readOnlyBannerMessage')}
              </span>
            </div>
          </div>
          <Link to="/subscription">
            <Button 
              variant="secondary" 
              size="sm"
              className="bg-white hover:bg-white/90 text-red-600 font-semibold transition-all hover:scale-105"
            >
              {t('trial.upgradeNow')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Active Trial Banner
  return (
    <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 text-white py-2.5 px-4 shadow-lg animate-in slide-in-from-top duration-300">
      <div className="container mx-auto flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 animate-pulse" />
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
            <span className="text-sm font-semibold">
              {t('trial.bannerActive')}
            </span>
            <span className="text-xs sm:text-sm opacity-90 flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {t('trial.daysRemaining', { count: daysRemaining })}
            </span>
          </div>
        </div>
        <Link to="/subscription">
          <Button 
            variant="secondary" 
            size="sm"
            className="bg-white/20 hover:bg-white/30 text-white border-white/40 font-medium backdrop-blur-sm transition-all hover:scale-105"
          >
            {t('trial.upgradeNow')}
          </Button>
        </Link>
      </div>
    </div>
  );
};